/**
 * Hide out-of-stock variant options dynamically (cascading approach)
 *
 * For each option at position N, availability is checked against
 * only the selected values of options at positions 1 through N-1.
 * This prevents deadlocks when variant matrices are incomplete
 * (e.g., Black only exists in Athletic fit, not Relaxed).
 *
 * After updating visibility, auto-selects the first visible value
 * for any option whose current selection became hidden — processed
 * in order so downstream options adjust correctly.
 *
 * Usage: Upload to Assets, then add to your product section:
 * <script src="{{ 'hide-sold-out-variants.js' | asset_url }}" defer="defer"></script>
 */

(function () {
  function init(container) {
    var productJSONEl = container.querySelector('[data-product-json]');
    if (!productJSONEl) return;

    var product = JSON.parse(productJSONEl.textContent);
    var variants = product.variants;
    var optionCount = product.options.length;

    if (optionCount < 1) return;

    // Collect all option wrappers sorted by position
    function getOptionWrappers() {
      var wrappers = [];
      container.querySelectorAll('[data-option-position]').forEach(function (w) {
        wrappers.push(w);
      });
      wrappers.sort(function (a, b) {
        return parseInt(a.dataset.optionPosition, 10) - parseInt(b.dataset.optionPosition, 10);
      });
      return wrappers;
    }

    // Read currently selected value for a given wrapper
    function getSelectedValue(wrapper) {
      var checked = wrapper.querySelector('input[data-single-option-selector]:checked');
      if (checked) return checked.value;

      var popout = wrapper.querySelector('[data-popout-input]');
      if (popout) return popout.value;

      return null;
    }

    // Build selected map { position: value }
    function getSelectedOptions(wrappers) {
      var selected = {};
      wrappers.forEach(function (w) {
        var pos = parseInt(w.dataset.optionPosition, 10);
        selected[pos] = getSelectedValue(w);
      });
      return selected;
    }

    /**
     * Cascading availability check:
     * For option at `position`, only match against selected values
     * of options with positions LOWER than `position`.
     *
     * Example with Color(1), Size(2), Fit(3):
     *   - Color: show if ANY available variant has this color
     *   - Size:  show if available variant has selected Color + this Size
     *   - Fit:   show if available variant has selected Color + selected Size + this Fit
     */
    function hasAvailableVariant(position, value, selected) {
      var optionIndex = position - 1;

      return variants.some(function (v) {
        if (!v.available) return false;
        if (v.options[optionIndex] !== value) return false;

        // Only check options with lower position numbers
        for (var p = 1; p < position; p++) {
          var idx = p - 1;
          if (selected[p] && v.options[idx] !== selected[p]) return false;
        }
        return true;
      });
    }

    // Hide/show a single element
    function setVisible(el, visible) {
      if (!el) return;
      el.style.display = visible ? '' : 'none';
    }

    // Get the visual parent of an input (swatch or button span)
    function getInputParent(input) {
      return input.closest('radio-swatch') || input.closest('.radio__button');
    }

    // Select first visible radio in a wrapper and fire change event
    function autoSelectFirstVisible(wrapper) {
      var current = wrapper.querySelector('input[data-single-option-selector]:checked');
      if (current) {
        var parent = getInputParent(current);
        if (parent && parent.style.display === 'none') {
          var allInputs = wrapper.querySelectorAll('input[data-single-option-selector]');
          for (var i = 0; i < allInputs.length; i++) {
            var par = getInputParent(allInputs[i]);
            if (par && par.style.display !== 'none') {
              allInputs[i].checked = true;
              allInputs[i].dispatchEvent(new Event('change', { bubbles: true }));
              return true; // selection changed
            }
          }
        }
      }

      // Handle popout/dropdown
      var popoutInput = wrapper.querySelector('[data-popout-input]');
      if (popoutInput && popoutInput.closest('[data-option-position]') === wrapper) {
        var currentVal = popoutInput.value;
        var items = wrapper.querySelectorAll('.select-popout__item');
        var currentHidden = false;

        items.forEach(function (li) {
          var link = li.querySelector('[data-value]');
          if (link && link.dataset.value === currentVal && li.style.display === 'none') {
            currentHidden = true;
          }
        });

        if (currentHidden) {
          for (var j = 0; j < items.length; j++) {
            if (items[j].style.display !== 'none') {
              var firstLink = items[j].querySelector('[data-value]');
              if (firstLink) {
                firstLink.click();
                return true;
              }
            }
          }
        }
      }

      return false;
    }

    function updateVisibility() {
      var wrappers = getOptionWrappers();
      var selected = getSelectedOptions(wrappers);

      // Process each option in position order (cascading)
      for (var w = 0; w < wrappers.length; w++) {
        var wrapper = wrappers[w];
        var position = parseInt(wrapper.dataset.optionPosition, 10);

        // --- Radio buttons & swatches ---
        wrapper.querySelectorAll('input[data-single-option-selector]').forEach(function (input) {
          var parent = getInputParent(input);
          if (!parent) return; // skip popout hidden inputs
          var visible = hasAvailableVariant(position, input.value, selected);
          setVisible(parent, visible);
        });

        // --- Popout / dropdown items ---
        wrapper.querySelectorAll('.select-popout__item').forEach(function (li) {
          var link = li.querySelector('[data-value]');
          if (!link) return;
          var visible = hasAvailableVariant(position, link.dataset.value, selected);
          setVisible(li, visible);
        });

        // --- Price-group containers ---
        wrapper.querySelectorAll('.radio-buttons__price-group').forEach(function (group) {
          var children = group.querySelectorAll('radio-swatch, .radio__button');
          var anyVisible = false;
          children.forEach(function (c) {
            if (c.style.display !== 'none') anyVisible = true;
          });
          setVisible(group, anyVisible);
        });

        // Auto-select first visible if current selection is hidden
        var changed = autoSelectFirstVisible(wrapper);
        if (changed) {
          // Re-read selected values since we just changed one;
          // this ensures downstream options use the updated value
          selected = getSelectedOptions(wrappers);
        }
      }
    }

    // --- Event listeners ---

    // Listen for radio/swatch changes
    container.addEventListener('change', function (e) {
      if (e.target.matches('[data-single-option-selector]')) {
        // Small delay to let the theme's own JS update the variant first
        setTimeout(updateVisibility, 60);
      }
    });

    // Listen for popout select changes (hidden input value changes)
    container.querySelectorAll('[data-popout-input]').forEach(function (input) {
      if (!input.closest('[data-option-position]')) return;
      var observer = new MutationObserver(function () {
        setTimeout(updateVisibility, 60);
      });
      observer.observe(input, { attributes: true, attributeFilter: ['value'] });
    });

    // Initial run
    updateVisibility();
  }

  // --- Boot ---
  function boot() {
    document.querySelectorAll('[data-section-type="product"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();